'use client';

import {
    createContext,
    useState,
    useEffect,
    useMemo,
    useCallback,
} from 'react';
import type { Dispatch, ReactNode, SetStateAction } from 'react';

import { LOYALTY_LOCAL_STORAGE_KEY, type Nullable } from '@libs/core';
import type {
    LoyaltyMemberPoints,
    Offer,
    PointsModel,
} from '@libs/common-types';
import apiProxyClient from '@libs/api-proxy-client';

import { filterOffersByType } from '../utils';
import { useLocalStorage } from '../hooks/useLocalStorage';
import { useUserSession } from '../hooks/useUserSession';

export interface LoyaltyContextProps {
    drawerOpen: boolean;
    featuredOffers: Nullable<Offer[]>;
    getOffers: () => Promise<void>;
    isActivating: boolean;
    memberPoints?: Nullable<LoyaltyMemberPoints>;
    offers: Nullable<Offer[]>;
    pointsHistory?: Nullable<PointsModel[]>;
    selectedOffer?: Offer;
    setDrawerOpen: Dispatch<SetStateAction<boolean>>;
    setIsActivating: Dispatch<SetStateAction<boolean>>;
    setSelectedOffer: Dispatch<SetStateAction<Offer | undefined>>;
    setPointsHistory?: (history: Nullable<PointsModel[]>) => void;
}

export type StorageLoyaltySession = {
    offers: Nullable<Offer[]>;
    featuredOffers: Nullable<Offer[]>;
    memberPoints: Nullable<LoyaltyMemberPoints>;
    pointsHistory: Nullable<PointsModel[]>;
};

export const initialLoyaltyState: LoyaltyContextProps = {
    drawerOpen: false,
    featuredOffers: undefined,
    getOffers: async () => undefined,
    isActivating: false,
    memberPoints: undefined,
    offers: undefined,
    pointsHistory: undefined,
    selectedOffer: undefined,
    setDrawerOpen: () => undefined,
    setIsActivating: () => undefined,
    setSelectedOffer: () => undefined,
    setPointsHistory: undefined,
};

export const LoyaltyContext =
    createContext<Nullable<LoyaltyContextProps>>(undefined);

export function LoyaltyProvider({ children }: { children: ReactNode }) {
    const { user } = useUserSession();
    const [storageLoyaltyData, setStorageLoyaltyData] =
        useLocalStorage<StorageLoyaltySession>(LOYALTY_LOCAL_STORAGE_KEY);

    const [offers, setOffers] = useState<Nullable<Offer[]>>(
        storageLoyaltyData?.offers || undefined
    );
    const [featuredOffers, setFeaturedOffers] = useState<Nullable<Offer[]>>(
        storageLoyaltyData?.featuredOffers || undefined
    );
    const [memberPoints, setMemberPoints] = useState<
        Nullable<LoyaltyMemberPoints>
    >(storageLoyaltyData?.memberPoints || undefined);
    const [pointsHistory, setPointsHistory] = useState<Nullable<PointsModel[]>>(
        storageLoyaltyData?.pointsHistory || undefined
    );

    const [drawerOpen, setDrawerOpen] = useState(false);
    const [isActivating, setIsActivating] = useState(false);
    const [selectedOffer, setSelectedOffer] = useState<Offer>();

    const getOffers = useCallback(async () => {
        const response = await apiProxyClient.loyalty.getOffers();
        const responseOffers = filterOffersByType({
            offers: response?.offers,
        });
        const responseFeaturedOffers = filterOffersByType({
            offers: response?.offers,
            type: 'Featured Offer',
        });

        setOffers(responseOffers);
        setFeaturedOffers(responseFeaturedOffers);
    }, []);

    useEffect(() => {
        // Initiate all requests in parallel
        const memberPointsPromise = apiProxyClient.loyalty.getMemberPoints();
        const pointsHistoryPromise = apiProxyClient.loyalty.getPointsHistory();
        const offersPromise = apiProxyClient.loyalty.getOffers();

        memberPointsPromise
            .then(response => {
                setMemberPoints(response);
                setStorageLoyaltyData(prev => ({
                    offers: prev?.offers || null,
                    featuredOffers: prev?.featuredOffers || null,
                    pointsHistory: prev?.pointsHistory || null,
                    memberPoints: response || null,
                }));
            })
            .catch(error => {
                console.log('Error fetching member points:', error);
            });

        pointsHistoryPromise
            .then(response => {
                setPointsHistory(response);
                setStorageLoyaltyData(prev => ({
                    offers: prev?.offers || null,
                    featuredOffers: prev?.featuredOffers || null,
                    memberPoints: prev?.memberPoints || null,
                    pointsHistory: response || null,
                }));
            })
            .catch(error => {
                console.log('Error fetching points history:', error);
            });

        offersPromise
            .then(response => {
                const responseOffers = filterOffersByType({
                    offers: response?.offers,
                });
                const responseFeaturedOffers = filterOffersByType({
                    offers: response?.offers,
                    type: 'Featured Offer',
                });

                setOffers(responseOffers);
                setFeaturedOffers(responseFeaturedOffers);
                setStorageLoyaltyData(prev => ({
                    ...prev,
                    offers: responseOffers || null,
                    featuredOffers: responseFeaturedOffers || null,
                    memberPoints: prev?.memberPoints || null,
                    pointsHistory: prev?.pointsHistory || null,
                }));
            })
            .catch(error => {
                console.log('Error fetching offers:', error);
            });
    }, [user]);

    const contextValue = useMemo(
        () => ({
            drawerOpen,
            featuredOffers,
            getOffers,
            isActivating,
            memberPoints,
            offers,
            pointsHistory,
            selectedOffer,
            setDrawerOpen,
            setIsActivating,
            setSelectedOffer,
            setPointsHistory,
        }),
        [
            drawerOpen,
            featuredOffers,
            getOffers,
            isActivating,
            memberPoints,
            offers,
            pointsHistory,
            selectedOffer,
            setDrawerOpen,
            setIsActivating,
            setSelectedOffer,
            setPointsHistory,
        ]
    );

    return (
        <LoyaltyContext.Provider value={contextValue}>
            {children}
        </LoyaltyContext.Provider>
    );
}
